import { useState } from "react";
import type { StudyDetail, StudyUpsertPayload } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { type LookupKey, type LookupRow, LookupSelect, type Lookups, parsePesos, pesosText } from "./CatalogueEditors.js";
import { Button, Field, Input, Textarea } from "./ui.js";

/** A clave as the catalogue stores it: trimmed and in capitals, like every imported clave. */
export const normalClave = (s: string) => s.trim().toUpperCase();

/**
 * «Nuevo estudio» (Catálogo → Estudios; pilot request of 2026-11-03, "una opción para agregar
 * algún estudio"). The ficha and its price on the general list in one form; the analytes are
 * added next, on the study page, with the same editor as any study (new ones or copies of
 * analytes that already exist). The clave is unique per laboratory whatever its capitals; the
 * server refuses a repeat, and the form warns before that with the claves it already knows.
 */
export function NewStudyForm({
  lookups,
  claves,
  priceList,
  onSaved,
  onCancel,
}: {
  lookups: Lookups;
  claves: string[];
  priceList: { id: string; name: string } | null;
  onSaved: (s: StudyDetail) => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [clave, setClave] = useState("");
  const [name, setName] = useState("");
  const [ids, setIds] = useState<Pick<StudyUpsertPayload, "area_id" | "sample_type_id" | "container_id" | "method_id">>({ area_id: null, sample_type_id: null, container_id: null, method_id: null });
  const [delivery, setDelivery] = useState("");
  const [indications, setIndications] = useState("");
  const [price, setPrice] = useState("");
  const [extra, setExtra] = useState<Partial<Record<LookupKey, LookupRow[]>>>({});
  const [busy, setBusy] = useState(false);

  const taken = new Set(claves.map(normalClave));
  const claveError = !clave.trim() ? null : normalClave(clave).length > 20 ? "La clave lleva hasta 20 caracteres." : taken.has(normalClave(clave)) ? `Ya existe un estudio con la clave ${normalClave(clave)}.` : null;
  const deliveryOk = delivery.trim() === "" || /^\d{1,3}$/.test(delivery.trim());
  const cents = price.trim() === "" ? null : parsePesos(price);
  const priceOk = price.trim() === "" || cents !== null;
  const ready = clave.trim() !== "" && name.trim() !== "" && claveError === null && deliveryOk && priceOk;
  const rowsOf = (k: LookupKey): LookupRow[] => [...(lookups[k] as LookupRow[]), ...(extra[k] ?? [])];
  const lookup = (k: LookupKey, label: string, field: keyof typeof ids) => (
    <LookupSelect label={label} kind={k} rows={rowsOf(k)} value={ids[field]} onChange={(id) => setIds((x) => ({ ...x, [field]: id }))} onCreated={(row) => setExtra((x) => ({ ...x, [k]: [...(x[k] ?? []), row] }))} />
  );

  async function save() {
    setBusy(true);
    try {
      const saved = await run<"catalogue.study.upsert", StudyDetail>("catalogue.study.upsert", {
        study_id: null,
        clave: normalClave(clave),
        name: name.trim(),
        kind: "analytic",
        ...ids,
        technique_id: null,
        equipment_id: null,
        indications: indications.trim() || null,
        delivery_days: delivery.trim() === "" ? null : Number(delivery.trim()),
        is_active: true,
      });
      if (cents !== null && priceList) {
        try {
          await run("catalogue.price.set", { price_list_id: priceList.id, study_id: saved.id, package_id: null, amount_cents: cents });
        } catch (e) {
          toast.error(`${saved.clave} se creó, pero el precio no se guardó: ${errorMessage(e)}`);
        }
      }
      toast.success(`${saved.clave} creado. Agrega sus analitos.`);
      onSaved(saved);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="grid-2">
        <Field label="Clave" hint="Única en el laboratorio." error={claveError}>
          <Input aria-label="Clave del estudio" className="mono" value={clave} onChange={(e) => setClave(e.target.value)} onBlur={() => setClave((c) => normalClave(c))} autoFocus />
        </Field>
        <Field label="Nombre del estudio">
          <Input aria-label="Nombre del estudio" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {lookup("areas", "Área", "area_id")}
        {lookup("sample_types", "Tipo de muestra", "sample_type_id")}
        {lookup("containers", "Recipiente", "container_id")}
        {lookup("methods", "Método", "method_id")}
        <Field label="Días de entrega" optional hint="0 = mismo día." error={deliveryOk ? null : "Escribe un número de días."}>
          <Input aria-label="Días de entrega" inputMode="numeric" value={delivery} onChange={(e) => setDelivery(e.target.value)} />
        </Field>
        {priceList && (
          <Field label={`Precio en ${priceList.name}`} optional error={priceOk ? null : "Escribe el precio en pesos, con hasta dos decimales."}>
            <Input aria-label="Precio en pesos" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={() => cents !== null && setPrice(pesosText(cents))} />
          </Field>
        )}
      </div>
      <Field label="Indicaciones para el paciente" optional hint="Se imprimen en el ticket.">
        <Textarea aria-label="Indicaciones para el paciente" rows={3} value={indications} onChange={(e) => setIndications(e.target.value)} />
      </Field>
      <p className="small muted">Después de crearlo agregas sus analitos: nuevos o copiados de otro estudio.</p>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={!ready} onClick={() => void save()}>
          Crear estudio
        </Button>
      </div>
    </div>
  );
}

